import React from 'react';
import Promise from 'bluebird';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { ImagePickerContainer } from '@bingads-webui-react/image-picker';
import { MediaRepository } from '@bingads-webui-campaign/media-repository';

import StockImageVendor from './stockImageVendor';
import StockImageItemWrapper from './stock-image-wrapper';
import { newImageFromMTObject } from '../../model/image-experience-image';
import {
  ImageSizeValidationError,
  validateImageWithDimension,
} from '../../util/image-size-validation';
import getCroppedImage from '../../util/get-cropped-image';
import { getSmartCroppedImagesAsync } from '../../util/get-smart-cropped-image';
import { StockPhotoDataSource, StockPhotoServiceError } from '../data-source/stock-photo-data-source';
import { ImageRepository } from '../../util/image-repository';
import LoadingPlaceholder from '../../components/loading-place-holder';
import C from '../../constants';

const itemHeight = 180;

class StockImagePickerContainer extends React.PureComponent {
  onLoadMore = () => {
    const { stockPhotoDataSource } = this.props;

    stockPhotoDataSource.loadMore();
  };

  onStockImageSelected = ([{ id }]) => {
    const {
      showUiBlocker,
      imageRepository,
      showErrors,
      onSelectedImagesSet,
      i18n,
      stockPhotoDataSource,
      isMultimediaAd,
      isAdCreatorEnabled,
      mediaRepository,
      isMultimediaAdinMSANEnabled,
      isChooseThumbnail,
      perfMarker,
      imageOption,
      aspectRatioOption,
      onSelectStockImage,
    } = this.props;

    perfMarker.willInit();

    showUiBlocker(true);

    if (onSelectStockImage) {
      onSelectStockImage();
    }

    stockPhotoDataSource.SaveAsync({
      stockImageId: id,
      stockImageVendorId: StockImageVendor.ShutterStock,
    })
      .then(({ value }) => imageRepository.getImageByIdAsync(value))
      .then((imgObjMT) => {
        const imgObj = newImageFromMTObject(imgObjMT, C.imageSources.Stock);
        const postValidationError = validateImageWithDimension(imgObj.Width, imgObj.Height);

        if (postValidationError) {
          throw postValidationError;
        }

        if (isChooseThumbnail) {
          return Promise.resolve(imgObjMT);
        }

        if (isAdCreatorEnabled) {
          return getSmartCroppedImagesAsync({
            mediaRepository,
            originalImageToCrop: imgObj,
            smartCropActivity: this.props.smartCropActivity,
            isMultimediaAd,
            isMultimediaAdinMSANEnabled,
            imageOption,
            aspectRatioOption,
          });
        }

        const croppedImages = getCroppedImage({
          originalImageToCrop: imgObj,
          isMultimediaAdinMSANEnabled,
          isMultimediaAd,
          imageOption,
          aspectRatioOption,
        });
        return Promise.resolve(croppedImages);
      })
      .then(imgObj => onSelectedImagesSet(imgObj))
      .catch((e) => {
        if (e instanceof ImageSizeValidationError) {
          const stringTemplateKey = 'ImageExperience_SizeValidationFailTemplate';

          showErrors([
            i18n.getString(stringTemplateKey, {
              width: e.minWidth,
              height: e.minHeight,
            }),
          ]);
        } else if (e instanceof StockPhotoServiceError) {
          const innerError = e.getInnerError();
          // StockImageLimitExceededPerCustomer
          if (!_.isEmpty(innerError)) {
            showErrors([
              i18n.getString(`ImageExperience_Error_${innerError}`),
            ]);
          } else {
            showErrors([
              i18n.getString('ImageExperience_Error_StockPhoto_Generic'),
            ]);
          }
        } else {
          throw e;
        }
      })
      .finally(() => {
        perfMarker.done();

        showUiBlocker(false);
      });
  };

  bingLoadingImage = () => {
    const { staticImagePath, isForVNext } = this.props;

    if (isForVNext) {
      return (
        <LoadingPlaceholder />
      );
    }

    const imgPath = `${staticImagePath}Loader_Grey_30x30.gif`;
    return (
      <div style={{ backgroundColor: 'white', width: '100%', height: '100%' }}>
        <img alt="loading" src={imgPath} style={{ marginTop: (itemHeight - 30) / 2 }} />
      </div>
    );
  };

  render() {
    const {
      staticImagePath,
      stockPhotoDataSource,
      i18n,
      onKeyboardEscapeFromImage,
    } = this.props;

    return (
      <div className="stock-image-picker-container">
        <ImagePickerContainer
          i18n={i18n}
          dataSource={stockPhotoDataSource}
          isMultiSelection={false}
          onLoadMore={this.onLoadMore}
          onSelectionChanged={this.onStockImageSelected}
          isAutoLoadMore
          isLazyLoadEnabled
          maximizeFrame
          itemHeight={itemHeight}
          imagePlaceHolder={this.bingLoadingImage}
          imageListItemWrapper={StockImageItemWrapper}
          staticImagePath={staticImagePath}
          useBingAdsLoader
          onEscapeFromImage={onKeyboardEscapeFromImage}
        />
      </div>
    );
  }
}

StockImagePickerContainer.propTypes = {
  stockPhotoDataSource: PropTypes.instanceOf(StockPhotoDataSource).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  staticImagePath: PropTypes.string,
  onSelectedImagesSet: PropTypes.func.isRequired,
  showErrors: PropTypes.func,
  imageRepository: PropTypes.instanceOf(ImageRepository).isRequired,
  showUiBlocker: PropTypes.func,
  isForVNext: PropTypes.bool,
  isMultimediaAd: PropTypes.bool,
  isMultimediaAdinMSANEnabled: PropTypes.bool,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  isAdCreatorEnabled: PropTypes.bool,
  isChooseThumbnail: PropTypes.bool,
  mediaRepository: PropTypes.instanceOf(MediaRepository),
  smartCropActivity: PropTypes.shape({
    trace: PropTypes.func,
  }),
  imageOption: PropTypes.string,
  aspectRatioOption: PropTypes.string,
  onKeyboardEscapeFromImage: PropTypes.func,
  onSelectStockImage: PropTypes.func,
};

StockImagePickerContainer.defaultProps = {
  showErrors: {},
  showUiBlocker: {},
  staticImagePath: '',
  isForVNext: false,
  isMultimediaAd: false,
  isAdCreatorEnabled: false,
  isMultimediaAdinMSANEnabled: false,
  isChooseThumbnail: false,
  mediaRepository: null,
  smartCropActivity: {
    trace: _.noop,
  },
  imageOption: C.defaultImageOption,
  aspectRatioOption: C.defaultAspectRatioOption,
  onKeyboardEscapeFromImage: _.noop,
  onSelectStockImage: null,
};

export default StockImagePickerContainer;
